import React, { useState, useEffect } from 'react';
import './App.css';

const ChartGuysRepository = ({ onNavigateHome }) => {
  const [chartGuys, setChartGuys] = useState([]);
  const [sortedGuys, setSortedGuys] = useState([]);
  const [sortBy, setSortBy] = useState('followers'); // default sort by followers
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Google Sheets CSV URL
  const SHEET_ID = '1-ZmdcBoa2QFIhysALgRTQBN5rkQuFaNuAJfbhrHiP1Q';
  const SHEET_URL = `https://docs.google.com/spreadsheets/d/${SHEET_ID}/export?format=csv`;

  useEffect(() => {
    fetchChartGuys();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    sortChartGuys();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartGuys, sortBy]);

  const fetchChartGuys = async () => {
    try {
      const response = await fetch(SHEET_URL);
      const csvText = await response.text();
      const parsedData = parseCSV(csvText);
      setChartGuys(parsedData);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching chart guys:', err);
      setError('Failed to load Chart Guys data');
      setLoading(false);
    }
  };

  const parseCSV = (csvText) => {
    const lines = csvText.split('\n');
    const data = [];
    
    // Skip header row (index 0)
    for (let i = 1; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line) continue;
      
      // Parse CSV line (handle quoted fields)
      const values = parseCSVLine(line);
      
      if (values.length >= 4) {
        // Extract Twitter handle (remove @ if present)
        let handle = values[1].trim();
        if (handle.startsWith('@')) {
          handle = handle.substring(1);
        }
        
        // Parse follower count
        const followers = parseInt(values[2]) || 0;
        
        data.push({
          name: values[0].trim(),
          handle: handle,
          followers: followers,
          description: values[3].trim(),
          profilePic: `https://unavatar.io/twitter/${handle}` // Using unavatar service for Twitter pics
        });
      }
    }
    
    return data;
  };

  const parseCSVLine = (line) => {
    const result = [];
    let current = '';
    let inQuotes = false;
    
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      
      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        result.push(current);
        current = '';
      } else {
        current += char;
      }
    }
    
    result.push(current);
    return result;
  };

  const sortChartGuys = () => {
    let sorted = [...chartGuys];
    
    switch (sortBy) {
      case 'name':
        sorted.sort((a, b) => a.name.localeCompare(b.name));
        break;
      case 'handle':
        sorted.sort((a, b) => a.handle.localeCompare(b.handle));
        break;
      case 'followers':
        sorted.sort((a, b) => b.followers - a.followers); // Descending
        break;
      default:
        break;
    }
    
    setSortedGuys(sorted);
  };

  const formatFollowers = (count) => {
    if (count >= 1000000) {
      return (count / 1000000).toFixed(1) + 'M';
    } else if (count >= 1000) {
      return (count / 1000).toFixed(1) + 'K';
    }
    return count.toString();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-chartguy-dark to-black flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-chartguy-yellow mx-auto mb-4"></div>
          <p className="text-chartguy-yellow text-xl">Loading Chart Guys...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-chartguy-dark to-black flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-400 text-xl mb-4">{error}</p>
          <button 
            onClick={onNavigateHome}
            className="bg-chartguy-yellow text-chartguy-dark px-6 py-2 rounded-full font-semibold hover:bg-chartguy-brown hover:text-white transition"
          >
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-chartguy-dark to-black">
      {/* Header */}
      <nav className="sticky top-0 z-50 bg-chartguy-dark/95 backdrop-blur-md border-b border-chartguy-yellow/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <button 
              onClick={onNavigateHome}
              className="flex items-center space-x-2 hover:opacity-80 transition"
            >
              <span className="text-xl font-display font-bold text-chartguy-yellow">← Back to Home</span>
            </button>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="section-container">
        {/* Title and Description */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-6xl font-display font-bold mb-6 gradient-text" data-testid="repository-title">
            Chart Guys Repository
          </h1>
          <p className="text-gray-300 text-lg max-w-4xl mx-auto leading-relaxed">
            This is a curated library of chart analysts ('Chart Guys') who embody the Chart Guy ethos: sharing insights, 
            educating others, and showing up through all market conditions. These individuals are not affiliated with the 
            Chart Guy project, but they've earned a nod of recognition for the value they provide. Think of this list as a 
            discovery tool, a place to find new, grounded voices worth following.
          </p>
        </div>

        {/* Sort Controls */}
        <div className="flex flex-wrap justify-center gap-4 mb-8">
          <span className="text-gray-400 self-center">Sort by:</span>
          <button
            onClick={() => setSortBy('name')}
            className={`px-4 py-2 rounded-full font-semibold transition ${
              sortBy === 'name' 
                ? 'bg-chartguy-yellow text-chartguy-dark' 
                : 'bg-chartguy-dark/50 text-gray-300 border border-chartguy-yellow/30 hover:border-chartguy-yellow/60'
            }`}
            data-testid="sort-by-name"
          >
            Name
          </button>
          <button
            onClick={() => setSortBy('handle')}
            className={`px-4 py-2 rounded-full font-semibold transition ${
              sortBy === 'handle' 
                ? 'bg-chartguy-yellow text-chartguy-dark' 
                : 'bg-chartguy-dark/50 text-gray-300 border border-chartguy-yellow/30 hover:border-chartguy-yellow/60'
            }`}
            data-testid="sort-by-handle"
          >
            Handle
          </button>
          <button
            onClick={() => setSortBy('followers')}
            className={`px-4 py-2 rounded-full font-semibold transition ${
              sortBy === 'followers' 
                ? 'bg-chartguy-yellow text-chartguy-dark' 
                : 'bg-chartguy-dark/50 text-gray-300 border border-chartguy-yellow/30 hover:border-chartguy-yellow/60'
            }`}
            data-testid="sort-by-followers"
          >
            Followers
          </button>
        </div>

        {/* Chart Guys Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-7xl mx-auto">
          {sortedGuys.map((guy, index) => (
            <a
              key={index}
              href={`https://twitter.com/${guy.handle}`}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-chartguy-dark/50 border border-chartguy-yellow/30 rounded-xl p-6 hover:border-chartguy-yellow hover:scale-105 transition-all duration-300 hover:shadow-xl hover:shadow-chartguy-yellow/20 cursor-pointer"
              data-testid={`chartguy-card-${index}`}
            >
              <div className="flex items-start gap-4">
                {/* Profile Picture */}
                <img 
                  src={guy.profilePic}
                  alt={guy.name}
                  className="w-16 h-16 rounded-full border-2 border-chartguy-yellow/30 flex-shrink-0"
                  onError={(e) => {
                    e.target.src = 'https://via.placeholder.com/64/A67C52/FFFFFF?text=CG';
                  }}
                />
                
                {/* Info */}
                <div className="flex-1 min-w-0">
                  <h3 className="text-xl font-display font-bold text-chartguy-yellow mb-1 truncate">
                    {guy.name}
                  </h3>
                  <p className="text-gray-400 text-sm mb-2">@{guy.handle}</p>
                  <div className="flex items-center gap-2 mb-3">
                    <svg className="w-4 h-4 text-chartguy-yellow" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z"/>
                    </svg>
                    <span className="text-gray-300 text-sm font-semibold">
                      {formatFollowers(guy.followers)} followers
                    </span>
                  </div>
                  <p className="text-gray-300 text-sm line-clamp-3">
                    {guy.description}
                  </p>
                </div>
              </div>
            </a>
          ))}
        </div>

        {/* Empty State */}
        {sortedGuys.length === 0 && !loading && (
          <div className="text-center py-12">
            <p className="text-gray-400 text-xl">No Chart Guys found</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChartGuysRepository;
